import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Crosshair, Zap, Gem } from 'lucide-react';

const fadeUpVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Discretion',
      description: 'We operate with complete confidentiality. Your strategies, challenges, and opportunities remain between us.'
    },
    {
      icon: Crosshair,
      title: 'Precision',
      description: 'Every recommendation is targeted, every resource allocated with purpose. No waste, no filler.'
    },
    {
      icon: Zap,
      title: 'Momentum',
      description: 'We build velocity into everything we do. Progress measured in weeks, not quarters.'
    },
    {
      icon: Gem,
      title: 'Craft',
      description: 'Excellence in execution. Whether strategy or creative, we hold ourselves to the highest standard.'
    }
  ];

  return (
    <main data-testid="about-page" className="pt-36 md:pt-40">
      {/* Hero Section */}
      <section 
        data-testid="about-hero"
        className="bg-ivory py-16 md:py-20"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-4xl"
            initial="hidden"
            animate="visible"
            variants={fadeUpVariant}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-6 block">
              About us
            </span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-medium text-charcoal tracking-tight leading-[1.05] mb-8">
              Built by Operators,<br />For Operators
            </h1>
            <p className="text-lg md:text-xl text-softcharcoal/80 font-light leading-relaxed max-w-3xl">
              Novum Royale emerged from decades of collective experience across commercial leadership, creative direction, and content strategy. We've sat in your seat.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section 
        data-testid="about-story"
        className="bg-ivory py-12 md:py-16"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <motion.div 
              className="lg:col-span-7"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUpVariant}
              transition={{ duration: 0.6 }}
            >
              <div className="space-y-8">
                <p className="text-lg md:text-xl text-softcharcoal font-light leading-relaxed">
                  <span className="font-heading font-medium text-charcoal">We understand the pressures of executive leadership.</span> The board expectations. The market dynamics. The need to move fast while maintaining quality.
                </p>
                <p className="text-base md:text-lg text-softcharcoal/80 font-light leading-relaxed">
                  Our founding team has led commercial and creative functions across multiple industries, from early-stage ventures to established enterprises. This operator mindset shapes everything we do—from how we scope engagements to how we measure success.
                </p>
                <p className="text-base md:text-lg text-softcharcoal/80 font-light leading-relaxed">
                  We built Novum Royale to be the partner we wished we had: strategic without being academic, creative without being indulgent, efficient without cutting corners.
                </p>
              </div>
            </motion.div>

            <motion.div 
              className="lg:col-span-5"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUpVariant}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <div className="bg-muted/50 border border-softcharcoal/5 p-8 md:p-10">
                <h3 className="font-heading text-2xl text-charcoal font-medium mb-6">
                  The Operator Difference
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-gold rounded-full mr-4 mt-2 flex-shrink-0"></span>
                    <span className="text-base text-softcharcoal/80 font-light">
                      We've built and scaled commercial teams
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-gold rounded-full mr-4 mt-2 flex-shrink-0"></span>
                    <span className="text-base text-softcharcoal/80 font-light">
                      We've led brand transformations under pressure
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-gold rounded-full mr-4 mt-2 flex-shrink-0"></span>
                    <span className="text-base text-softcharcoal/80 font-light">
                      We've created content systems that scale
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-gold rounded-full mr-4 mt-2 flex-shrink-0"></span>
                    <span className="text-base text-softcharcoal/80 font-light">
                      We speak your language
                    </span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section - Dark */}
      <section 
        data-testid="about-values"
        className="bg-charcoal text-ivory py-16 md:py-20"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUpVariant}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-6 block">
              What guides us
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-ivory tracking-tight font-medium">
              Our Values
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                data-testid={`value-${index + 1}`}
                className="group"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeUpVariant}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="w-12 h-12 flex items-center justify-center border border-ivory/20 mb-6 group-hover:border-gold transition-colors duration-300">
                  <value.icon size={24} className="text-gold" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl text-ivory font-medium mb-4">
                  {value.title}
                </h3>
                <p className="text-sm text-ivory/60 font-light leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Operating Model Section */}
      <section 
        data-testid="about-model"
        className="bg-ivory py-16 md:py-20"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <motion.div 
              className="lg:col-span-5"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUpVariant}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-6 block">
                How we operate
              </span>
              <h2 className="font-heading text-4xl md:text-5xl text-charcoal tracking-tight font-medium mb-8">
                Our Model
              </h2>
              <p className="text-lg text-softcharcoal/80 font-light leading-relaxed">
                Novum Royale brings together specialist ventures and trusted partners under a unified approach.
              </p>
            </motion.div>

            <motion.div 
              className="lg:col-span-7"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUpVariant}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <div className="space-y-6">
                <div className="bg-white/50 border border-softcharcoal/5 p-8 md:p-10">
                  <h3 className="font-heading text-xl text-charcoal font-medium mb-4">
                    Senior Attention
                  </h3>
                  <p className="text-base text-softcharcoal/70 font-light leading-relaxed">
                    Every engagement is led by experienced practitioners who've held senior positions in commercial, creative, and content roles. You work directly with decision-makers, not intermediaries.
                  </p>
                </div>
                
                <div className="bg-white/50 border border-softcharcoal/5 p-8 md:p-10">
                  <h3 className="font-heading text-xl text-charcoal font-medium mb-4">
                    Lean Execution
                  </h3>
                  <p className="text-base text-softcharcoal/70 font-light leading-relaxed">
                    We assemble right-sized teams for each project. No bloated hierarchies, no unnecessary overhead. Every resource is purposefully deployed toward your objectives.
                  </p>
                </div>

                <div className="bg-white/50 border border-softcharcoal/5 p-8 md:p-10">
                  <h3 className="font-heading text-xl text-charcoal font-medium mb-4">
                    Integrated Approach
                  </h3>
                  <p className="text-base text-softcharcoal/70 font-light leading-relaxed">
                    Whether you need sales strategy, brand development, or content systems, our capabilities work together. Single point of contact, unified strategy, coordinated execution.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Promise Section */}
      <section 
        data-testid="about-promise"
        className="bg-charcoal text-ivory py-16 md:py-20"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariant}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-6 block">
              Our promise
            </span>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-ivory tracking-tight font-medium mb-8 italic">
              "Senior attention,<br />lean execution."
            </h2>
            <p className="text-lg text-ivory/60 font-light leading-relaxed mb-10">
              This isn't a tagline—it's our operating principle. We believe ambitious organisations deserve strategic partners who combine deep expertise with efficient delivery.
            </p>
            <Link
              to="/contact"
              data-testid="about-cta"
              className="inline-flex items-center space-x-3 bg-gold text-white px-10 py-4 text-sm uppercase tracking-widest font-medium hover:bg-goldhover transition-colors duration-300"
            >
              <span>Start a Conversation</span>
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default About;
